import React, { useRef, useState, Fragment } from "react";
import {
    Button,
    Typography,
    Stack,
    Tooltip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    ImageList,
    ImageListItem,
    Paper,
    Divider,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import AddLinkIcon from "@mui/icons-material/AddLink";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";

import { Box } from "@mui/system";
import Loader from "src/components/loader/Loader";
import axios from "axios";
import { useTranslation } from 'react-i18next';
import { FileDrop } from "react-file-drop";

const IMAGE_TYPES = ["png", "jpg", "jpeg"];

const VIDEO_TYPES = ["mp4", "mov", "wmv", "avi", "flv", "mkv"];

const isMediaFile = (fileName, type) => {
    if (!fileName) {
        return false;
    }
    const fileExt = fileName.split(".").pop();
    let imageExts = VIDEO_TYPES.concat(IMAGE_TYPES);
    if (type && typeof type === "string") {
        if (type.toLowerCase() === "image") {
            imageExts = IMAGE_TYPES;
        } else if (type.toLowerCase() === "video") {
            imageExts = VIDEO_TYPES;
        }
    }
    return imageExts.includes(fileExt);
};

const ThumbnailStep = ({
    attachments,
    uploadedFiles = [],
    thumbnail,
    setThumbnail,
    showErrorMessage,
}) => {
    const [loading, setLoading] = useState(false);
    const mediaFiles = attachments.filter((file) => isMediaFile(file.FileName));
    const [open, setOpen] = useState(false);
    const [frames, setFrames] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const fileInputRef = useRef(null);
    const { t } = useTranslation();

    const uploadThumbnail = async (files) => {
        let file = files[0];
        const name = file.name.replace(/\s+/g, "_").toLowerCase();
        const size = file.size;
        const type = file.type;
        const toBase64 = (file) =>
            new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => resolve(reader.result);
                reader.onerror = (error) => reject(error);
            });
        setLoading(true);
        try {
            file = await toBase64(file);
            setThumbnail({
                id: null,
                name: name,
                size: size,
                type: type,
                file: file,
            });
        } catch (err) {
            showErrorMessage(err);
        } finally {
            setLoading(false);
        }
    };
    const removeThumbnail = () => {
        setThumbnail(null);
    };

    const getFrameData = async (videoFiles) => {
        const fps = 1;
        let tempFrames = [];
        for (const videoFile of videoFiles) {
            const videoBlob =
                videoFile.Id >= 0
                    ? await fetch(
                          `${axios.defaults.baseURL}${videoFile.FilePath}`
                      ).then((r) => r.blob())
                    : videoFile.File;

            let videoObjectUrl = URL.createObjectURL(videoBlob);
            let video = document.createElement("video");

            let seekResolve;
            video.addEventListener("seeked", async function () {
                if (seekResolve) seekResolve();
            });

            video.src = videoObjectUrl;

            // workaround chromium metadata bug (https://stackoverflow.com/q/38062864/993683)
            while (
                (video.duration === Infinity || isNaN(video.duration)) &&
                video.readyState < 2
            ) {
                await new Promise((r) => setTimeout(r, 1000));
                video.currentTime = 10000000 * Math.random();
            }
            let duration = video.duration;

            let canvas = document.createElement("canvas");
            let context = canvas.getContext("2d");
            let [w, h] = [video.videoWidth, video.videoHeight];
            canvas.width = w;
            canvas.height = h;

            let interval = 1 / fps;
            let currentTime = 0;

            while (currentTime < duration) {
                video.currentTime = currentTime;
                await new Promise((r) => (seekResolve = r));

                context.drawImage(video, 0, 0, w, h);
                let base64ImageData = canvas.toDataURL();

                const stringLength =
                    base64ImageData.length - "data:image/png;base64,".length;

                const sizeInBytes =
                    4 * Math.ceil(stringLength / 3) * 0.5624896334383812;
                tempFrames.push({
                    file: base64ImageData,
                    name:
                        videoFile.FileName.substr(
                            0,
                            videoFile.FileName.lastIndexOf(".")
                        ) + "png",
                    type: "image/png",
                    size: sizeInBytes,
                });

                currentTime += interval;
            }
        }
        return tempFrames;
    };

    const selectFromAttachments = async () => {
        const videoFiles = mediaFiles.filter((file) =>
            isMediaFile(file.FileName, "video")
        );
        const imageFiles = mediaFiles.filter((file) =>
            isMediaFile(file.FileName, "image")
        );
        if (videoFiles.length < 1 && imageFiles.length < 1) {
            return;
        }
        setSelectedIndex(-1);
        const imageFrames = imageFiles.map((file) => {
            const rawFile = file.File;
            const uploadedMatch = uploadedFiles.find(
                (uploaded) =>
                    uploaded.name &&
                    file.FileName &&
                    uploaded.name.toLowerCase() === file.FileName.toLowerCase()
            );
            const preview =
                file.Id >= 0
                    ? `${axios.defaults.baseURL}${file.FilePath}`
                    : uploadedMatch && uploadedMatch.file
                    ? uploadedMatch.file
                    : rawFile instanceof Blob
                    ? URL.createObjectURL(rawFile)
                    : rawFile;
            const sizeValue =
                uploadedMatch && typeof uploadedMatch.size === "number"
                    ? uploadedMatch.size
                    : typeof file.FileSize === "number"
                    ? file.FileSize
                    : rawFile && typeof rawFile.size === "number"
                    ? rawFile.size
                    : 0;
            return {
                id:
                    typeof file.Id === "number" && file.Id > 0
                        ? file.Id
                        : typeof file.id === "number" && file.id > 0
                        ? file.id
                        : null,
                file: preview,
                rawFile: rawFile instanceof Blob ? rawFile : null,
                name: file.FileName,
                type: file.Type || "image",
                size: sizeValue,
            };
        });
        if (videoFiles.length < 1) {
            setFrames(imageFrames);
            setOpen(true);
            return;
        }
        setLoading(true);
        const tempFrames = await getFrameData(videoFiles);
        setFrames([...imageFrames, ...tempFrames]);
        setLoading(false);
        setOpen(true);
    };

    const handleSelectThumbnail = async () => {
        const frame = frames[selectedIndex];
        if (!frame) return;
        let fileData = frame.file;
        if (!frame.id && frame.rawFile instanceof Blob) {
            fileData = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(frame.rawFile);
            });
        }
        setThumbnail({
            id: frame.id ?? null,
            name: frame.name,
            size: frame.size,
            type: frame.type,
            file: fileData,
        });
        handleClose();
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleFilePick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const getPreviewSrc = (thumb) => {
        if (!thumb || !thumb.file) return null;
        if (
            thumb.file.startsWith("data:") ||
            thumb.file.startsWith("http://") ||
            thumb.file.startsWith("https://") ||
            thumb.file.startsWith("blob:")
        ) {
            return thumb.file;
        }
        if (thumb.file instanceof Blob) {
            return URL.createObjectURL(thumb.file);
        }
        return thumb.id ? `${axios.defaults.baseURL}${thumb.file}` : thumb.file;
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
            }}
            onDragOver={(event) => {
                event.preventDefault();
            }}
            onDrop={(event) => {
                event.preventDefault();
                event.stopPropagation();
            }}
        >
            {loading ? (
                <Loader />
            ) : (
                <Fragment>
                    <Dialog open={open} onClose={handleClose}>
                        <DialogTitle>
                            {t("ThumbnailStep.dialogTitle")}
                        </DialogTitle>
                        <DialogContent>
                            <ImageList
                                sx={{ width: 500 }}
                                cols={3}
                                // rowHeight={164}
                            >
                                {frames.map((item, index) => (
                                    <ImageListItem
                                        onClick={() => {
                                            setSelectedIndex(index);
                                        }}
                                        key={index}
                                        sx={{
                                            ":hover": {
                                                transform: "scale(1.1)",
                                            },
                                            ...(selectedIndex === index && {
                                                // borderColor: "blue",
                                                opacity: 0.5,
                                            }),
                                        }}
                                    >
                                        <img
                                            src={`${item.file}`}
                                            srcSet={`${item.file}`}
                                            // alt={item.title}
                                            loading="lazy"
                                            // onMouseOver={(e) =>
                                            //     style={{}}
                                            // }
                                        />
                                    </ImageListItem>
                                ))}
                            </ImageList>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleClose}>{t('buttonCommons.cancel')}</Button>
                            <Button
                                disabled={selectedIndex < 0 ? true : false}
                                onClick={() => {
                                    handleSelectThumbnail();
                                }}
                            >
                                {t('buttonCommons.submit')}
                            </Button>
                        </DialogActions>
                    </Dialog>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            width: "100%",
                            maxWidth: 760,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 2,
                            background:
                                "linear-gradient(135deg, rgba(0,0,0,0.02), rgba(0,0,0,0.01))",
                        }}
                    >
                        <Stack spacing={2.5}>
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                    {t("ThumbnailStep.title")}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {thumbnail && thumbnail.name
                                        ? t("ThumbnailStep.thumbnail", { name: thumbnail.name })
                                        : t("ThumbnailStep.noThumbnail")}
                                </Typography>
                            </Box>
                            <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="stretch">
                                <Paper
                                    variant="outlined"
                                    onDragOver={(event) => {
                                        event.preventDefault();
                                    }}
                                    onDrop={(event) => {
                                        event.preventDefault();
                                        if (event.dataTransfer?.files?.length) {
                                            uploadThumbnail(event.dataTransfer.files);
                                        }
                                    }}
                                    sx={{
                                        flex: "0 0 auto",
                                        width: { xs: "100%", md: 220 },
                                        height: { xs: 180, md: 160 },
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        borderStyle: "dashed",
                                        borderColor: "divider",
                                        background: "rgba(0,0,0,0.01)",
                                        position: "relative",
                                        overflow: "hidden",
                                    }}
                                >
                                    {thumbnail ? (
                                        <img
                                            style={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "contain",
                                                background: "rgba(0,0,0,0.02)",
                                            }}
                                            src={getPreviewSrc(thumbnail)}
                                            alt="thumbnail"
                                        />
                                    ) : (
                                        <Stack
                                            spacing={1}
                                            alignItems="center"
                                            justifyContent="center"
                                            sx={{ p: 2 }}
                                        >
                                            <ImageOutlinedIcon
                                                color="disabled"
                                                sx={{ fontSize: 36 }}
                                            />
                                            <Typography variant="body2" color="text.secondary">
                                                {t("ThumbnailStep.previewPlaceholder")}
                                            </Typography>
                                        </Stack>
                                    )}
                                </Paper>
                                <Stack spacing={1.5} sx={{ flex: 1, minWidth: { md: 260 } }}>
                                    <FileDrop onDrop={uploadThumbnail}>
                                        <Paper
                                            variant="outlined"
                                            onClick={handleFilePick}
                                            sx={{
                                                p: 2,
                                                cursor: "pointer",
                                                borderStyle: "dashed",
                                                borderColor: "primary.main",
                                                background: "rgba(25, 118, 210, 0.04)",
                                                transition: "all 0.2s ease",
                                                "&:hover": {
                                                    background: "rgba(25, 118, 210, 0.08)",
                                                },
                                            }}
                                        >
                                            <Stack spacing={0.5}>
                                                <Typography variant="subtitle2">
                                                    {t("ThumbnailStep.dropzoneTitle")}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    {t("ThumbnailStep.dropzoneHint")}
                                                </Typography>
                                            </Stack>
                                        </Paper>
                                    </FileDrop>
                                    <Divider />
                                    <Stack spacing={1}>
                                        <Button
                                            variant="contained"
                                            startIcon={<UploadFileIcon />}
                                            onClick={handleFilePick}
                                            fullWidth
                                        >
                                            {t("ThumbnailStep.upload")}
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            startIcon={<AddLinkIcon />}
                                            onClick={selectFromAttachments}
                                            fullWidth
                                        >
                                            {t("ThumbnailStep.selectFromAttachments")}
                                        </Button>
                                        <Button
                                            variant="text"
                                            color="error"
                                            startIcon={<DeleteIcon />}
                                            onClick={removeThumbnail}
                                            fullWidth
                                            disabled={!thumbnail}
                                        >
                                            {t("buttonCommons.delete")}
                                        </Button>
                                    </Stack>
                                </Stack>
                            </Stack>
                            <input
                                ref={fileInputRef}
                                style={{ display: "none" }}
                                id="upload-thumbnail"
                                name="upload-thumbnail"
                                type="file"
                                accept="image/png,image/jpeg,image/jpg"
                                onChange={(e) => {
                                    uploadThumbnail(e.target.files);
                                }}
                            />
                        </Stack>
                    </Paper>
                </Fragment>
            )}
        </Box>
    );
};

export default ThumbnailStep;
